import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, Calendar, ArrowLeft, Facebook, Linkedin, Twitter, MessageCircle, UserRound } from 'lucide-react';
import Seo from '../components/Seo';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import JsonLd from '../components/JsonLd';
import {
  getPostBySlug,
  getRelatedPosts,
  getReadingTime,
  getCategoryLabel,
  formatPostDate,
  blogAuthor,
} from '../data/blogPosts';

const WHATSAPP_PHONE = '522292072733';

const ContentBlock = ({ block }) => {
  switch (block.type) {
    case 'h2':
      return <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">{block.text}</h2>;
    case 'h3':
      return <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-3">{block.text}</h3>;
    case 'h4':
      return <h4 className="text-lg font-bold text-gray-900 mt-6 mb-2">{block.text}</h4>;
    case 'tip':
      return (
        <aside className="bg-orange-50 border-l-4 border-orange-500 rounded-r-xl px-5 py-4 my-6 text-gray-800 leading-relaxed">
          {block.text}
        </aside>
      );
    case 'ul':
      return (
        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    default:
      return <p className="text-gray-700 leading-relaxed mb-4">{block.text}</p>;
  }
};

const ShareButtons = ({ url, title }) => {
  const links = [
    {
      id: 'whatsapp',
      label: 'Compartir en WhatsApp',
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    },
    {
      id: 'facebook',
      label: 'Compartir en Facebook',
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      id: 'x',
      label: 'Compartir en X',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    },
    {
      id: 'linkedin',
      label: 'Compartir en LinkedIn',
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
  ];

  return (
    <div className="flex items-center gap-3" data-testid="blog-share-buttons">
      <span className="text-sm font-semibold text-gray-700">Compartir:</span>
      {links.map(({ id, label, icon: Icon, href }) => (
        <a
          key={id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          data-testid={`share-${id}`}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-orange-500 hover:text-white transition-colors"
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
    </div>
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const url = `https://www.gensolver.com/blog/${post.slug}`;
  const related = getRelatedPosts(post);

  const postSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: 'es-MX',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: {
      '@type': 'Person',
      name: blogAuthor.name,
      description: blogAuthor.role,
      worksFor: { '@type': 'Organization', name: 'GENSOL' },
    },
    publisher: {
      '@type': 'Organization',
      name: 'GENSOL',
      url: 'https://www.gensolver.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://customer-assets.emergentagent.com/job_power-equipment/artifacts/wolxjy6h_LOGO%20GENSOL%20SIN%20FONDO.png',
      },
    },
  };

  return (
    <>
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        canonical={url}
        ogType="article"
        ogImage={post.image}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Inicio', url: 'https://www.gensolver.com/' },
          { name: 'Blog', url: 'https://www.gensolver.com/blog' },
          { name: post.title, url },
        ]}
      />
      <JsonLd data={postSchema} />

      <main data-page="blog-post" data-analytics-page={`blog-${post.slug}`} className="pt-24 bg-white">
        <article itemScope itemType="https://schema.org/BlogPosting">
          {/* Article header */}
          <header className="container mx-auto px-4 pt-10 max-w-3xl">
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-orange-500 transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" /> Volver al blog
            </Link>
            <span className="block">
              <Link
                to="/blog"
                className="inline-block text-xs font-semibold uppercase tracking-wide text-orange-600 bg-orange-100 px-3 py-1 rounded-full mb-4"
              >
                {getCategoryLabel(post.category)}
              </Link>
            </span>
            <h1
              data-testid="blog-post-h1"
              itemProp="headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-5"
            >
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500 mb-8">
              <span className="flex items-center gap-1.5">
                <UserRound className="w-4 h-4 text-orange-500" />
                <span itemProp="author">{blogAuthor.name}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-orange-500" />
                <time dateTime={post.date} itemProp="datePublished">
                  {formatPostDate(post.date)}
                </time>
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-orange-500" />
                {getReadingTime(post)} min de lectura
              </span>
            </div>
          </header>

          {/* Featured image */}
          <figure className="container mx-auto px-4 max-w-4xl mb-10">
            <img
              src={post.image}
              alt={post.imageAlt}
              itemProp="image"
              width="1200"
              height="600"
              className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-md"
            />
          </figure>

          {/* Body */}
          <div
            className="container mx-auto px-4 max-w-3xl"
            itemProp="articleBody"
            data-testid="blog-post-body"
          >
            {post.content.map((block, i) => (
              <ContentBlock key={i} block={block} />
            ))}
          </div>

          {/* WhatsApp CTA */}
          {post.ctaText && (
            <div className="container mx-auto px-4 max-w-3xl mt-10">
              <div
                data-testid="blog-post-cta"
                className="bg-gray-900 rounded-2xl p-8 text-center"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  {post.ctaText}
                </h2>
                <p className="text-gray-300 mb-6 max-w-xl mx-auto">
                  Recibe asesoría gratuita de un especialista y tu cotización formal por WhatsApp.
                </p>
                <a
                  href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(post.ctaMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="blog-cta-whatsapp"
                  data-analytics-click={`blog-cta-whatsapp-${post.slug}`}
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold transition-all hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5" />
                  Cotizar por WhatsApp
                </a>
              </div>
            </div>
          )}

          {/* Share */}
          <div className="container mx-auto px-4 max-w-3xl mt-10 pt-6 border-t border-gray-200">
            <ShareButtons url={url} title={post.title} />
          </div>

          {/* Author box - E-E-A-T */}
          <div className="container mx-auto px-4 max-w-3xl mt-10">
            <div
              data-testid="blog-author-box"
              className="bg-gray-100 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-5 items-start"
            >
              <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <UserRound className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-600 mb-1">
                  Escrito por
                </p>
                <p className="text-lg font-bold text-gray-900">{blogAuthor.name}</p>
                <p className="text-sm text-gray-600 font-medium mb-2">{blogAuthor.role}</p>
                <p className="text-sm text-gray-600">{blogAuthor.description}</p>
              </div>
            </div>
          </div>
        </article>

        {/* Related posts */}
        {related.length > 0 && (
          <section className="bg-gray-100 mt-14 py-14" data-testid="blog-related-section">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                Artículos relacionados
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {related.map((rp) => (
                  <article
                    key={rp.slug}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
                  >
                    <Link to={`/blog/${rp.slug}`} className="block overflow-hidden">
                      <img
                        src={rp.image}
                        alt={rp.imageAlt}
                        loading="lazy"
                        width="600"
                        height="280"
                        className="w-full h-44 object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </Link>
                    <div className="p-6">
                      <h3 className="text-base font-bold text-gray-900 leading-snug mb-2">
                        <Link
                          to={`/blog/${rp.slug}`}
                          data-testid={`related-link-${rp.slug}`}
                          className="hover:text-orange-500 transition-colors"
                        >
                          {rp.title}
                        </Link>
                      </h3>
                      <p className="text-gray-600 text-sm">{rp.excerpt}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
};

export default BlogPostPage;
